/**
 * Phone Management Modal Component for AmalQ
 * Comprehensive phone number management with verification flow
 * Following enterprise-grade patterns and accessibility standards
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Phone, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { usePhoneManagement } from '../../../../entities/user/hooks/usePhone';
import { PhoneInputComponent } from '../../../../shared/ui/inputs/PhoneInput';
import { VerificationCodeInput } from '../../../../shared/ui/inputs/VerificationCodeInput';
import { useToastActions } from '../../../../shared/ui/toast';

/**
 * Modal states for phone management flow
 */
type ModalState = 'add' | 'verify' | 'change' | 'remove-confirm';

/**
 * Props interface for PhoneManagementModal component
 */
interface PhoneManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialState?: ModalState;
  currentPhone?: string;
  onSuccess?: () => void;
}

/**
 * Phone Management Modal Component
 * Production-grade modal for phone number management
 * 
 * Features:
 * - Add phone number with verification
 * - Change existing phone number
 * - Remove phone number with confirmation
 * - Real-time validation and error handling
 * - Accessibility compliant (WCAG 3.0 AA)
 * - Mobile-responsive design
 * 
 * @param isOpen - Whether modal is open
 * @param onClose - Callback to close modal
 * @param initialState - Initial modal state
 * @param currentPhone - Current phone number (for change/remove)
 * @param onSuccess - Callback when operation succeeds
 */
export const PhoneManagementModal: React.FC<PhoneManagementModalProps> = ({
  isOpen,
  onClose,
  initialState = 'add',
  currentPhone,
  onSuccess,
}) => {
  const [currentState, setCurrentState] = useState<ModalState>(initialState);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isValidPhone, setIsValidPhone] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [error, setError] = useState<string>('');

  // Hooks
  const phoneManagement = usePhoneManagement();
  const toast = useToastActions();

  /**
   * Reset modal state when opened
   */
  useEffect(() => {
    if (isOpen) {
      setCurrentState(initialState);
      setPhoneNumber(currentPhone || '');
      setVerificationCode('');
      setIsValidPhone(false);
      setCountdown(0);
      setResendCooldown(0);
      setError('');
    }
  }, [isOpen, initialState, currentPhone]);

  /**
   * Handle countdown timer
   */
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [countdown]);

  /**
   * Handle resend cooldown timer
   */
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendCooldown]);

  /**
   * Handle phone number validation
   */
  const handlePhoneValidation = useCallback((isValid: boolean) => {
    setIsValidPhone(isValid);
    if (isValid) {
      setError('');
    }
  }, []);

  /**
   * Extract error message from various error types
   * Handles Error instances, PhoneApiError objects, and unknown errors
   */
  const extractErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error) {
      return error.message;
    }
    // Handle PhoneApiError objects thrown by phoneApi
    if (error && typeof error === 'object' && 'message' in error) {
      return (error as { message: string }).message;
    }
    return fallback;
  };

  /**
   * Handle add phone number
   */
  const handleAddPhone = useCallback(async () => {
    if (!isValidPhone || !phoneNumber) return;

    try {
      setError('');
      const response = await phoneManagement.addPhone.mutateAsync({
        phoneNumber,
      });

      if (response.success) {
        setCountdown(response.data?.expiresIn || 600); // 10 minutes default
        setCurrentState('verify');
        toast.success('Verification Code Sent', 'Please check your phone for the verification code.');
      }
    } catch (error: unknown) {
      console.error('Failed to add phone:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to send verification code. Please try again.');
      setError(errorMessage);
      toast.error('Verification Failed', errorMessage);
    }
  }, [isValidPhone, phoneNumber, phoneManagement.addPhone, toast]);

  /**
   * Handle verify phone number
   */
  const handleVerifyPhone = useCallback(async () => {
    if (!verificationCode || verificationCode.length !== 6) return;

    try {
      setError('');
      const response = await phoneManagement.verifyPhone.mutateAsync({
        phoneNumber,
        code: verificationCode,
      });

      if (response.success) {
        toast.success('Phone Verified! 🎉', 'Your phone number has been successfully verified.');
        onSuccess?.();
        onClose();
      }
    } catch (error: unknown) {
      console.error('Failed to verify phone:', error);
      const errorMessage = extractErrorMessage(error, 'Invalid verification code. Please try again.');
      setError(errorMessage);
      toast.error('Verification Failed', errorMessage);
    }
  }, [verificationCode, phoneNumber, phoneManagement.verifyPhone, toast, onSuccess, onClose]);

  /**
   * Handle change phone number
   */
  const handleChangePhone = useCallback(async () => {
    if (!isValidPhone || !phoneNumber) return;

    try {
      setError('');
      const response = await phoneManagement.changePhone.mutateAsync({
        newPhoneNumber: phoneNumber,
      });

      if (response.success) {
        setCountdown(response.data?.expiresIn || 600); // 10 minutes default
        setCurrentState('verify');
        toast.success('Verification Code Sent', 'Please check your new phone for the verification code.');
      }
    } catch (error: unknown) {
      console.error('Failed to change phone:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to send verification code. Please try again.');
      setError(errorMessage);
      toast.error('Verification Failed', errorMessage);
    }
  }, [isValidPhone, phoneNumber, phoneManagement.changePhone, toast]);

  /**
   * Handle remove phone number
   */
  const handleRemovePhone = useCallback(async () => {
    try {
      setError('');
      const response = await phoneManagement.removePhone.mutateAsync();

      if (response.success) {
        toast.success('Phone Removed', 'Your phone number has been successfully removed.');
        onSuccess?.();
        onClose();
      }
    } catch (error: unknown) {
      console.error('Failed to remove phone:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to remove phone number. Please try again.');
      setError(errorMessage);
      toast.error('Removal Failed', errorMessage);
    }
  }, [phoneManagement.removePhone, toast, onSuccess, onClose]);

  /**
   * Handle resend verification code
   */
  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0) return;

    try {
      setError('');
      setResendCooldown(30); // 30 second cooldown
      
      // Resend based on current state
      if (currentState === 'verify') {
        await phoneManagement.addPhone.mutateAsync({ phoneNumber });
      } else if (currentState === 'change') {
        await phoneManagement.changePhone.mutateAsync({ newPhoneNumber: phoneNumber });
      }
      
      toast.success('Code Resent', 'A new verification code has been sent to your phone.');
    } catch (error: unknown) {
      console.error('Failed to resend code:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to resend verification code. Please try again.');
      setError(errorMessage);
      toast.error('Resend Failed', errorMessage);
    }
  }, [resendCooldown, currentState, phoneNumber, phoneManagement.addPhone, phoneManagement.changePhone, toast]);

  /**
   * Handle back to phone input
   */
  const handleBackToPhone = useCallback(() => {
    setCurrentState(initialState);
    setVerificationCode('');
    setCountdown(0);
    setError('');
  }, [initialState]);

  /**
   * Get modal title based on current state
   */
  const getModalTitle = (): string => {
    switch (currentState) {
      case 'add':
        return 'Add Phone Number';
      case 'verify':
        return 'Verify Phone Number';
      case 'change':
        return 'Change Phone Number';
      case 'remove-confirm':
        return 'Remove Phone Number';
      default:
        return 'Phone Management';
    }
  };

  /**
   * Get modal description based on current state
   */
  const getModalDescription = (): string => {
    switch (currentState) {
      case 'add':
        return 'Add your phone number to secure your account and enable SMS notifications';
      case 'verify':
        return 'Enter the 6-digit verification code sent to your phone';
      case 'change':
        return 'Enter your new phone number to replace the current one';
      case 'remove-confirm':
        return 'Are you sure you want to remove your phone number? This will disable SMS notifications and 2FA via SMS.';
      default:
        return '';
    }
  };

  /**
   * Render phone input state
   */
  const renderPhoneInput = (): React.JSX.Element => (
    <div className="space-y-6">
      <PhoneInputComponent
        value={phoneNumber}
        onChange={setPhoneNumber}
        onValidationChange={handlePhoneValidation}
        error={error}
        label="Phone Number"
        helperText="Enter your phone number with country code"
        placeholder="+1 (555) 123-4567"
        required
      />

      <div className="flex gap-3">
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
        >
          Cancel
        </motion.button>
        <motion.button
          onClick={currentState === 'add' ? handleAddPhone : handleChangePhone}
          disabled={!isValidPhone || phoneManagement.isAddingPhone || phoneManagement.isChangingPhone}
          whileHover={{ scale: !isValidPhone ? 1 : 1.02 }}
          whileTap={{ scale: !isValidPhone ? 1 : 0.98 }}
          className={cn(
            "flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2",
            !isValidPhone || phoneManagement.isAddingPhone || phoneManagement.isChangingPhone
              ? "bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed"
              : "bg-linear-to-r from-blue-500 to-indigo-500 text-white hover:shadow-lg"
          )}
        >
          {phoneManagement.isAddingPhone || phoneManagement.isChangingPhone ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Phone className="w-4 h-4" />
              <span>{currentState === 'add' ? 'Send Code' : 'Continue'}</span>
            </>
          )}
        </motion.button>
      </div>
    </div>
  );

  /**
   * Render verification code state
   */
  const renderVerificationCode = (): React.JSX.Element => (
    <div className="space-y-6">
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-3">
          <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <div>
            <div className="font-medium text-blue-900 dark:text-blue-100">
              Code sent to {phoneNumber}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              Check your phone for the verification code
            </div>
          </div>
        </div>
      </div>

      <VerificationCodeInput
        value={verificationCode}
        onChange={setVerificationCode}
        onComplete={handleVerifyPhone}
        error={error}
        label="Verification Code"
        helperText="Enter the 6-digit code from your phone"
        countdown={countdown}
        onResend={handleResendCode}
        resendCooldown={resendCooldown}
        autoSubmit={true}
      />

      <div className="flex gap-3">
        <motion.button
          onClick={handleBackToPhone}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
        >
          Back
        </motion.button>
        <motion.button
          onClick={handleVerifyPhone}
          disabled={verificationCode.length !== 6 || phoneManagement.isVerifyingPhone}
          whileHover={{ scale: verificationCode.length === 6 ? 1.02 : 1 }}
          whileTap={{ scale: verificationCode.length === 6 ? 0.98 : 1 }}
          className={cn(
            "flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2",
            verificationCode.length !== 6 || phoneManagement.isVerifyingPhone
              ? "bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed"
              : "bg-linear-to-r from-green-500 to-emerald-500 text-white hover:shadow-lg"
          )}
        >
          {phoneManagement.isVerifyingPhone ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>Verify</span>
            </>
          )}
        </motion.button>
      </div>
    </div>
  );

  /**
   * Render remove confirmation state
   */
  const renderRemoveConfirmation = (): React.JSX.Element => (
    <div className="space-y-6">
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <div>
            <div className="font-medium text-red-900 dark:text-red-100">
              This action cannot be undone
            </div>
            <div className="text-sm text-red-700 dark:text-red-300">
              Removing your phone will disable SMS notifications and 2FA via SMS
            </div>
          </div>
        </div>
      </div>

      <div className="text-center">
        <div className="font-medium text-gray-900 dark:text-white mb-2">
          Current Phone Number
        </div>
        <div className="text-lg font-mono text-gray-600 dark:text-gray-400">
          {currentPhone}
        </div>
      </div>

      <div className="flex gap-3">
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
        >
          Cancel
        </motion.button>
        <motion.button
          onClick={handleRemovePhone}
          disabled={phoneManagement.isRemovingPhone}
          whileHover={{ scale: phoneManagement.isRemovingPhone ? 1 : 1.02 }}
          whileTap={{ scale: phoneManagement.isRemovingPhone ? 1 : 0.98 }}
          className={cn(
            "flex-1 px-4 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2",
            phoneManagement.isRemovingPhone
              ? "bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed"
              : "bg-linear-to-r from-red-500 to-red-600 text-white hover:shadow-lg"
          )}
        >
          {phoneManagement.isRemovingPhone ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>Removing...</span>
            </>
          ) : (
            <>
              <X className="w-4 h-4" />
              <span>Remove Phone</span>
            </>
          )}
        </motion.button>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-2 sm:mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-linear-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 id="modal-title" className="font-jakarta font-bold text-lg text-gray-900 dark:text-white">
                    {getModalTitle()}
                  </h3>
                  <p id="modal-description" className="font-ui text-gray-600 dark:text-gray-400 text-sm">
                    {getModalDescription()}
                  </p>
                </div>
              </div>
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <X className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </motion.button>
            </div>

            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentState}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {currentState === 'add' && renderPhoneInput()}
                {currentState === 'change' && renderPhoneInput()}
                {currentState === 'verify' && renderVerificationCode()}
                {currentState === 'remove-confirm' && renderRemoveConfirmation()}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Default export for convenience
 */
export default PhoneManagementModal;
